import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY } from 'rxjs';
import { environment } from '../../environments/environment';

export interface IReply {
  _id:                string;
  body:               string;
  isEdited:           boolean;
  replyCount:         number;
  createdAt:          string;
  ableToModification: boolean;
  user: {
    _id:          string;
    displayName:  string;
    userName:     string;
    profileImage: { urlSmall?: string; urlMedium?: string } | null;
  };
}

export interface IComment {
  _id:                string;
  body:               string;
  isEdited:           boolean;
  replyCount:         number;
  createdAt:          string;
  ableToReply:        boolean;
  ableToModification: boolean;
  user: {
    _id:          string;
    displayName:  string;
    userName:     string;
    profileImage: { urlSmall?: string; urlMedium?: string } | null;
  };
  replies: IReply[];
}

export interface ICreateCommentPayload {
  targetType: 'EVENT' | 'POST' | 'BLOG';
  targetId:   string;
  parentId?:  string;
  body:       string;
}

interface IGetCommentsResponse {
  success: boolean;
  data: {
    items: IComment[];
    meta: {
      page:       number;
      limit:      number;
      total:      number;
      totalPages: number;
    };
  };
}

@Injectable({ providedIn: 'root' })
export class CommentService {

  private http       = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private base       = `${environment.apiUrl}/comments`;

  getComments(targetId: string, page = 1, targetType: 'EVENT' | 'POST' | 'BLOG' = 'EVENT', limit = 20) {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    const params = new HttpParams()
      .set('targetType', targetType)
      .set('targetId', targetId)
      .set('page', page)
      .set('limit', limit);
    return this.http.get<IGetCommentsResponse>(this.base, { params });
  }

  createComment(payload: ICreateCommentPayload) {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.post<{ success: boolean; data: IComment }>(this.base, payload);
  }

  updateComment(commentId: string, body: string) {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.patch<{ success: boolean; data: IComment }>(`${this.base}/${commentId}`, { body });
  }

  deleteComment(commentId: string) {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.delete<{ success: boolean }>(`${this.base}/${commentId}`);
  }
}